import { type RPPDomain } from '@retailer-platform/dashboard/core'
import { AdminNavEntryPointV3, NavEntryPointV3 } from '@retailer-platform/dashboard/routing'
import { onboardingAdminRouter } from './routing/onboardingAdminRouter'
import { onboardingRouter } from './routing/onboardingRouter'
import { onboardingPartnerScopedRouter } from './routing/onboardingPartnerScopedRouter'
import { onboardingStoreConfigScopedRouter } from './routing/onboardingStoreConfigScopedRouter'
import { onboardingPublicRouter } from './routing/onboardingPublicRouter'
import { type DomainMessages } from './utils/domain/intl'
import { routes } from './routing/routes'
import EN_US from './translations/en-US.json'
import { type DomainAccessControlConfig } from './utils/domain/accessControl'
import {
  flipDashboardAccessControl,
  isRelayOnboardingEnvEnabled,
  mobileManagementViewAccessControl,
  relayOnboardingAdminAccessControl,
  storeManagementMultiScopeAccessControl,
  storeManagementSingleScopeNavAccessControl,
  whitelabelSiteCustomizableStringsViewAccessControl,
} from './access-control/onboardingAccess.configuration'
import { OnboardingVisibilityWrapper } from './access-control/OnboardingVisibilityWrapper'

const definition: RPPDomain<typeof routes, DomainMessages, DomainAccessControlConfig> = {
  id: 'onboarding',
  totem: {
    // The default IPP tool of all routes in this domain - should match the Navigation Entry in IPP UI.
    // Individual routes can override this by specifying a `tool` prop on their DomainRoute component
    tool: 'onboarding-dashboard-tool',
  },
  routes,
  translations: { EN_US },
  lazyTranslations: {
    ES_ES: () => import('./translations/es-ES.json'),
    FR_FR: () => import('./translations/fr-FR.json'),
  },
  routers: {
    scoped: [onboardingStoreConfigScopedRouter, onboardingRouter],
    partnerScoped: [onboardingPartnerScopedRouter],
    admin: [onboardingAdminRouter],
    public: [onboardingPublicRouter],
  },
  navigationEntries: [
    {
      attachToV3: NavEntryPointV3.Stores,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'Manage store locations and details during onboarding',
          labelId: 'onboardingDomain.navItem.storeManagement',
          labelIdV3: 'onboardingDomain.navItem.storeManagement.v3',
          route: 'onboarding-store-management',
          subRoutes: ['onboarding-store-detail'],
          accessControl: storeManagementSingleScopeNavAccessControl,
          NavItemWrapper: OnboardingVisibilityWrapper,
          positionNavV3: 1,
        },
        {
          type: 'item',
          llmDescription: 'Manage store locations and details during onboarding',
          labelId: 'onboardingDomain.navItem.storeManagement',
          labelIdV3: 'onboardingDomain.navItem.storeManagement.v3',
          route: 'onboarding-store-management-partner-scoped',
          subRoutes: ['onboarding-store-detail', 'onboarding-request-new-store-partner-scoped'],
          accessControl: storeManagementMultiScopeAccessControl,
          NavItemWrapper: OnboardingVisibilityWrapper,
          positionNavV3: 1.5,
        },
      ],
    },
    {
      attachToV3: NavEntryPointV3.StorefrontPro,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'Customize and manage text strings displayed throughout the storefront',
          labelId: 'onboardingDomain.navItem.stringCustomization',
          labelIdV3: 'onboardingDomain.navItem.stringCustomization.v3',
          route: 'onboarding-string-customizations-storefront-pro',
          accessControl: whitelabelSiteCustomizableStringsViewAccessControl,
          isNew: true,
          positionNavV3: 18,
        },
      ],
    },
  ],
  adminNavigationEntries: [
    {
      attachToV3: AdminNavEntryPointV3.PartnerSettings,
      subNavItems: [
        {
          route: 'app-admin-onboarding-mobile-dashboard',
          subRoutes: ['app-admin-onboarding-mobile-app-detail'],
          labelId: 'onboardingDomain.mobileDashboard.navLabel',
          labelIdV3: 'onboardingDomain.mobileDashboard.navLabel.v3',
          accessControl: mobileManagementViewAccessControl,
          // Group 2 of v3 Partner settings: divider above, then Mobile / FLIP /
          // Entitlements at positions 4–6.
          positionNavV3: 4,
          dividerBefore: true,
        },
        {
          route: 'app-admin-onboarding-flip-dashboard',
          labelId: 'onboardingDomain.flipDashboard.navLabel',
          labelIdV3: 'onboardingDomain.flipDashboard.navLabel.v3',
          accessControl: flipDashboardAccessControl,
          positionNavV3: 5,
          subRoutes: [
            'app-admin-onboarding-flip-site-detail',
            'app-admin-onboarding-flip-feature-detail',
          ],
        },
        ...(isRelayOnboardingEnvEnabled
          ? [
              {
                route: 'app-admin-onboarding-relay-onboarding' as const,
                labelId: 'onboardingDomain.relayOnboarding.navLabel' as const,
                labelIdV3: 'onboardingDomain.relayOnboarding.navLabel.v3' as const,
                accessControl: relayOnboardingAdminAccessControl,
                positionNavV3: 6,
              },
            ]
          : []),
      ],
    },
  ],
}

export default definition
